import {
    IChannel,
    IDailyBudgetModalData,
    TBudgetUpdateModules,
    UtilsModule,
} from "@/ts/common-types";
import { Button, Modal, Popover } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateCpmModalDisplay } from "@/features/modalSlice";
import {
    getActiveModule,
    internationalizeNumericValue,
    parseInternationalizedValue,
} from "@/ts/utils";
import { setUpdateGridData } from "@/ts/filters";
import { showToast } from "@/ts/modals";
import { DATE_BUDGET_UPDATE_CONFIG } from "@/ts/constants";
import { useGetModulesData } from "@/hooks/AllCustomHooks";
import $ from "jquery";

type ICpmUpdateModalProps = {
    show: boolean;
};

const CpmUpdateModal: React.FC<ICpmUpdateModalProps> = ({ show }) => {
    const dispatch = useDispatch();

    const { activeModule } = getActiveModule();
    const componentConfig =
        useGetModulesData({ type: "component-config" }) || {};

    const rowData = useSelector(
        (state: IDailyBudgetModalData) => state.dailyBudgetModalData.value
    );

    const currencySymbol = useSelector(
        (state: IChannel) => state.channelCurrencySymbol.value
    );

    const [inputValue, setInputValue] = useState<string | number>(rowData.cpm_bid);

    const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = useState(false);

    const identifier =
        rowData[
            DATE_BUDGET_UPDATE_CONFIG[activeModule as TBudgetUpdateModules]
                .updateRecordId
        ];

    const [cpmBidLimitMessage, setCpmBidLimitMessage] = useState<string>("");

    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const parsedInputValue = +parseInternationalizedValue(inputValue);
        setIsDisabled(parsedInputValue === rowData.cpm_bid);
    }, [inputValue, rowData.cpm_bid]);

    const handleCpmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const changedCpmBid = e.target.value;
        if (changedCpmBid < rowData.min_bid_value) {
            setCpmBidLimitMessage(
                `Enter a value above ${currencySymbol}${rowData.min_bid_value}`
            );
            $(`#${identifier}-cpm-bid-limit-span`).show();
            setIsSubmitBtnDisabled(true);
        } else if (changedCpmBid > rowData.max_bid_value) {
            setCpmBidLimitMessage(
                `Enter a value less than  ${currencySymbol}${rowData.max_bid_value}`
            );
            $(`#${identifier}-cpm-bid-limit-span`).show();
            setIsSubmitBtnDisabled(true);
        } else {
            setCpmBidLimitMessage("");
            $(`#${identifier}-cpm-bid-limit-span`).hide();
            setIsSubmitBtnDisabled(false);
        }
    };

    const onApplyClickHandler = async () => {
        const { updateFunc, updateRecordId, updateRecordKey, recordNameKey } =
            DATE_BUDGET_UPDATE_CONFIG[activeModule as TBudgetUpdateModules];

        try {
            setLoading(true);
            const parsedInputValue = +parseInternationalizedValue(inputValue);

            const updateRecords = [
                {
                    [updateRecordKey]: rowData[updateRecordId].toString(),
                    attribute: ["cpm"],
                    state: rowData.state,
                    cpm: +parsedInputValue,
                },
            ];

            const updateMethod = (
                (await import("../../ts/utils")) as UtilsModule
            )[updateFunc];
            const response = await updateMethod(updateRecords);

            if (!response.status) {
                throw new Error(response.message);
            }

            setUpdateGridData({
                params: {
                    data: rowData,
                },
                value: parsedInputValue,
                isReduxState: true,
                modalParamToChange: "cpm",
            });

            showToast("success", response.message);
            dispatch(updateCpmModalDisplay(false));
            setLoading(false);
        } catch (err) {
            setLoading(false);
            setInputValue(rowData.cpm_bid);
            const resErrMsg = (err as Error).message;
            const errorMessage =
                ` Error While Updating ${componentConfig["cpm"].label} for ${rowData[recordNameKey]}` +
                `${resErrMsg && ": " + resErrMsg}`;
            showToast("danger", errorMessage);
        }
    };

    const handleClose = () => {
        dispatch(updateCpmModalDisplay(false));
    };

    const ModalHeaderElement = () => (
        <div className="d-flex align-items-center my-1 pb-2 fs-5">
            <div style={{ fontWeight: "600" }}>
                {`${componentConfig["cpm"].label}`}
            </div>
            {`${componentConfig["cpm"].label}` === "CPM" && (
                <Popover
                    className="mx-3"
                    title="Enter the amount that you are willing to pay for a thousand impressions on your Ads"
                    styles={{
                        body: {
                            padding: 15,
                            width: 300,
                        },
                    }}
                    placement="right"
                >
                    <InfoCircleOutlined />
                </Popover>
            )}
        </div>
    );

    return (
        <Modal
            open={show}
            centered
            title={<ModalHeaderElement />}
            onOk={handleClose}
            onCancel={handleClose}
            footer={null}
            width="30vw"
            styles={{
                body: {
                    height: "25%",
                    overflowY: "auto",
                },
            }}
        >
            <div style={{ padding: "1.5rem" }}>
                <div className="input-group-sm">
                    <input
                        className="form-control"
                        type="text"
                        style={{ width: "200px", height: "3rem" }}
                        onFocus={() =>
                            setInputValue(
                                parseInternationalizedValue(inputValue)
                            )
                        }
                        onBlur={() =>
                            setInputValue(
                                internationalizeNumericValue(inputValue)
                            )
                        }
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            handleCpmChange(e);
                        }}
                    />
                </div>
                <div
                    className="my-2 align-items-center text-danger"
                    style={{
                        fontSize: "0.9rem",
                        display: "none",
                    }}
                    id={`${rowData.sys_adgroup_id}-cpm-bid-limit-span`}
                >
                    <span className="">{cpmBidLimitMessage}</span>
                </div>
                <Button
                    className="btn btn-primary"
                    id="updateCpmBtn"
                    style={{
                        fontSize: "1rem",
                        height: 45,
                        width: 120,
                        marginTop: "2rem",
                    }}
                    loading={loading}
                    disabled={isDisabled || isSubmitBtnDisabled}
                    onClick={onApplyClickHandler}
                >
                    Apply
                </Button>
            </div>
        </Modal>
    );
};

export default CpmUpdateModal;
